const CLINICAL_READ = ['patient.read', 'encounter.read.own', 'rx.read', 'order.read'];
export const ROLE_TEMPLATES = [
    {
        code: 'super_admin',
        name: 'Super Admin',
        description: 'Top administrator within the hospital. Full control of tenant configuration.',
        scope: 'tenant',
        require2fa: true,
        permissions: ['*'],
    },
    {
        code: 'branch_admin',
        name: 'Branch Admin',
        description: 'Administers a single branch; cannot change tenant-wide plan or create higher admins.',
        scope: 'branch',
        require2fa: true,
        permissions: [
            'tenant.settings.read', 'branch.read', 'department.read', 'department.manage',
            'user.read', 'user.manage', 'user.reset_password', 'user.force_logout', 'user.manage_2fa', 'role.read', 'audit.read',
            'patient.read', 'patient.register', 'patient.update', 'patient.document.manage', 'patient.consent.manage',
            'appointment.read', 'appointment.manage', 'schedule.read', 'schedule.manage', 'token.issue',
            'encounter.read', 'order.read', 'rx.read', 'emr.config.manage',
            'admission.read', 'admission.manage', 'bed.read', 'bed.manage',
            'charge.read', 'invoice.read', 'invoice.create', 'invoice.cancel',
            'invoice.credit_note', 'invoice.line.remove', 'invoice.price.override',
            'payment.collect', 'payment.refund.approve', 'discount.apply', 'discount.approve', 'pharmacy.return.approve',
            'shift.manage', 'shift.supervise', 'deposit.manage', 'ward.charge.manage', 'dues.read', 'dues.manage',
            'visitor.locate', 'mortality.read', 'patient.deceased.revert', 'discharge.override',
            'product.read', 'stock.read', 'pharmacy.read',
            'stock.adjust.approve', 'supplier.payment.approve', 'stock.reprice',
            'lab.order.read', 'radiology.worklist',
            'claim.read', 'report.read.all', 'report.financial', 'dashboard.read',
            'notification.read', 'notification.send', 'task.read', 'task.manage',
        ],
    },
    {
        code: 'doctor',
        modules: ['opd', 'ipd', 'er', 'ot'],
        name: 'Consultant / Doctor',
        description: 'Full EMR write for own and assigned patients, prescriptions and orders.',
        scope: 'self',
        require2fa: false,
        permissions: [
            'patient.read', 'appointment.read.own', 'schedule.read', 'schedule.manage.own', 'mortality.read',
            'encounter.read.own', 'encounter.write', 'encounter.close', 'encounter.cosign', 'encounter.cover',
            'vitals.write', 'diagnosis.write', 'rx.write', 'rx.read', 'wasfaty.use',
            'order.write', 'order.read', 'certificate.issue',
            'admission.read', 'admission.manage', 'bed.read',
            'cpoe.write', 'emar.read', 'discharge.summary.write',
            'er.read', 'er.manage',
            'ot.read', 'ot.manage',
            'maternity.manage', 'therapy.manage', 'dental.manage',
            'lab.order.read', 'radiology.worklist',
            'invoice.read', 'report.read', 'dashboard.read', 'hr.self.read',
        ],
    },
    {
        code: 'medical_officer',
        modules: ['opd', 'ipd', 'er'],
        name: 'Medical Officer',
        description: 'Resident/house officer. As doctor, but orders may require a configured co-sign.',
        scope: 'self',
        require2fa: false,
        permissions: [
            'patient.read', 'appointment.read.own', 'encounter.read.own', 'encounter.write',
            'vitals.write', 'diagnosis.write', 'rx.write', 'rx.read', 'order.write', 'order.read',
            'admission.read', 'bed.read', 'cpoe.write', 'emar.read', 'nursing.note.write',
            'er.read', 'er.manage',
            'maternity.manage', 'therapy.manage',
            'report.read', 'dashboard.read', 'hr.self.read',
        ],
    },
    {
        code: 'nurse',
        modules: ['opd', 'ipd', 'er'],
        name: 'Nurse',
        description: 'Vitals, nursing notes, eMAR administration, sample flags and bedside tasks.',
        scope: 'department',
        require2fa: false,
        permissions: [
            ...CLINICAL_READ, 'appointment.read', 'vitals.write', 'nursing.note.write',
            'admission.read', 'bed.read', 'emar.read', 'emar.administer',
            'lab.order.read', 'lab.collect', 'indent.request',
            'er.read', 'er.manage',
            'maternity.manage', 'therapy.manage',
            'task.read', 'task.manage', 'report.read', 'dashboard.read', 'hr.self.read',
        ],
    },
    {
        code: 'head_nurse',
        modules: ['ipd'],
        name: 'Head Nurse',
        description: 'Nurse plus ward roster, ward bed management and ward stock requisition.',
        scope: 'department',
        require2fa: false,
        permissions: [
            ...CLINICAL_READ, 'appointment.read', 'vitals.write', 'nursing.note.write',
            'admission.read', 'admission.manage', 'bed.read', 'bed.manage',
            'emar.read', 'emar.administer', 'lab.order.read', 'lab.collect',
            'indent.request', 'stock.read', 'hr.roster.manage',
            'housekeeping.manage', 'visitor.locate', 'mortality.read', 'discharge.override',
            'er.read', 'er.manage',
            'task.read', 'task.manage', 'report.read', 'dashboard.read', 'hr.self.read',
        ],
    },
    {
        code: 'ot_nurse',
        modules: ['ot'],
        name: 'Operation Theatre Nurse',
        description: 'Theatre list, WHO checklist, swab and instrument counts, consumables and post-op notes.',
        scope: 'department',
        require2fa: false,
        permissions: [
            'patient.read', 'ot.read', 'ot.manage', 'vitals.write', 'nursing.note.write',
            'admission.read', 'bed.read', 'emar.read', 'indent.request', 'stock.read',
            'task.read', 'task.manage', 'report.read', 'dashboard.read', 'hr.self.read',
        ],
    },
    {
        code: 'receptionist',
        name: 'Receptionist',
        description: 'Registration, appointments and token issue. No clinical data.',
        scope: 'branch',
        require2fa: false,
        permissions: [
            'patient.read', 'patient.register', 'patient.update',
            'patient.document.manage', 'patient.consent.manage',
            'appointment.read', 'appointment.manage', 'schedule.read', 'token.issue', 'visitor.locate',
            'invoice.read', 'invoice.create', 'payment.collect', 'shift.manage', 'discount.apply',
            'charge.read', 'lab.order.create', 'lab.order.read', 'lab.homecollection.assign', 'radiology.order.create',
            'coverage.verify', 'nphies.eligibility', 'notification.send', 'report.read', 'dashboard.read', 'hr.self.read',
        ],
    },
    {
        code: 'cashier',
        modules: ['billing'],
        name: 'Cashier / Billing Officer',
        description: 'Invoices, payments, limited refunds and shift closing.',
        scope: 'branch',
        require2fa: true,
        permissions: [
            'patient.read', 'appointment.read', 'charge.read',
            'invoice.read', 'invoice.create', 'invoice.credit_note', 'payment.collect', 'payment.refund',
            'discount.apply', 'shift.manage', 'deposit.manage', 'dues.read', 'dues.manage',
            'report.read', 'dashboard.read', 'hr.self.read',
        ],
    },
    {
        code: 'pharmacist',
        modules: ['pharmacy', 'inventory'],
        name: 'Pharmacist',
        description: 'Pharmacy POS, prescription queue, dispensing, stock and returns.',
        scope: 'department',
        require2fa: false,
        permissions: [
            'patient.read', 'rx.read', 'pharmacy.pos', 'pharmacy.read', 'rx.dispense', 'rx.substitute', 'wasfaty.use',
            'pharmacy.return', 'indent.issue', 'pharmacy.controlled', 'pharmacy.report',
            'product.read', 'product.manage', 'stock.read', 'stock.adjust',
            'stock.take.manage', 'stock.batch.manage', 'stock.reprice', 'store.manage',
            'invoice.read', 'invoice.create', 'payment.collect', 'discount.apply', 'shift.manage',
            'report.read', 'dashboard.read', 'hr.self.read',
        ],
    },
    {
        code: 'lab_technician',
        modules: ['lab'],
        name: 'Lab Technician',
        description: 'Sample accession and result entry. Cannot verify or release results.',
        scope: 'department',
        require2fa: false,
        permissions: [
            'patient.read', 'lab.order.read', 'lab.collect', 'lab.result.enter',
            'lab.critical.acknowledge', 'lab.homecollection.assign', 'lab.instrument.view',
            'report.read', 'dashboard.read', 'hr.self.read',
        ],
    },
    {
        code: 'pathologist',
        modules: ['lab'],
        name: 'Pathologist / Lab Manager',
        description: 'Result verification and release, report authoring, lab QC and test master.',
        scope: 'department',
        require2fa: false,
        permissions: [
            'patient.read', 'lab.order.read', 'lab.collect', 'lab.result.enter',
            'lab.result.verify', 'lab.master.manage',
            'lab.result.amend', 'lab.qc.override', 'lab.critical.acknowledge',
            'lab.release.override', 'lab.homecollection.assign',
            'lab.settings.manage', 'lab.order.unbilled',
            'lab.collect.identity.override', 'lab.result.edit.approve', 'lab.label.reprint.direct',
            'lab.label.reprint.approve', 'lab.instrument.view', 'lab.instrument.manage',
            'report.read', 'dashboard.read', 'hr.self.read',
        ],
    },
    {
        code: 'radiology_technician',
        modules: ['radiology'],
        name: 'Radiology Technician',
        description: 'Modality worklist and scan status capture.',
        scope: 'department',
        require2fa: false,
        permissions: ['patient.read', 'radiology.worklist', 'radiology.critical.acknowledge', 'report.read', 'dashboard.read', 'hr.self.read'],
    },
    {
        code: 'radiologist',
        modules: ['radiology'],
        name: 'Radiologist',
        description: 'Radiology reporting, verification and release.',
        scope: 'department',
        require2fa: false,
        permissions: [
            'patient.read', 'radiology.worklist', 'radiology.report.write',
            'radiology.report.verify', 'radiology.master.manage', 'radiology.critical.acknowledge',
            'radiology.order.unbilled', 'radiology.release.override',
            'radiology.import.manage', 'radiology.second_opinion',
            'report.read', 'dashboard.read', 'hr.self.read',
        ],
    },
    {
        code: 'insurance_officer',
        modules: ['insurance'],
        name: 'Insurance / Panel Officer',
        description: 'Panel and TPA claims, pre-authorisation and coverage verification.',
        scope: 'branch',
        require2fa: false,
        permissions: [
            'patient.read', 'payer.manage', 'coverage.verify', 'claim.read', 'claim.manage',
            'preauth.manage', 'invoice.read', 'charge.read', 'nphies.use', 'integration.read',
            'nphies.eligibility', 'nphies.preauth.send', 'nphies.claim.send', 'nphies.status', 'nphies.communication',
            'nphies.payment.notice', 'nphies.poll.now', 'nphies.deadletter.apply', 'nphies.messages.read', 'nphies.cancel', 'nphies.inbound.link',
            'report.read', 'dashboard.read', 'hr.self.read',
        ],
    },
    {
        code: 'store_keeper',
        modules: ['inventory'],
        name: 'Store Keeper',
        description: 'General and pharmacy inventory, GRN, issues, transfers and purchase requests.',
        scope: 'branch',
        require2fa: false,
        permissions: [
            'product.read', 'product.manage', 'stock.read', 'stock.adjust',
            'purchase.manage', 'purchase.grn', 'purchase.return', 'store.manage', 'indent.issue',
            'stock.take.manage', 'stock.batch.manage',
            'report.read', 'dashboard.read', 'hr.self.read',
        ],
    },
    {
        code: 'hr_officer',
        modules: ['hr'],
        name: 'HR Officer',
        description: 'Staff records, attendance, leave and payroll inputs.',
        scope: 'tenant',
        require2fa: true,
        permissions: [
            'user.read', 'hr.employee.read', 'hr.employee.manage', 'hr.roster.manage',
            'hr.attendance.manage', 'hr.leave.approve', 'hr.payroll.run', 'hr.self.read',
            'report.read', 'dashboard.read',
        ],
    },
    {
        code: 'accountant',
        modules: ['accounting'],
        name: 'Accountant',
        description: 'General ledger, ledgers and financial reports. No clinical data.',
        scope: 'tenant',
        require2fa: true,
        permissions: [
            'charge.read', 'invoice.read', 'payment.refund.approve', 'shift.supervise',
            'gl.read', 'gl.manage', 'payout.manage', 'authority.read', 'doctor_share.read',
            'supplier.payment.create', 'supplier.payment.approve', 'stock.adjust.approve',
            'stock.read', 'claim.read', 'claim.settle', 'remittance.post', 'payer_invoice.issue', 'dues.read',
            'report.read.all', 'report.financial', 'dashboard.read', 'hr.self.read',
        ],
    },
    {
        code: 'auditor',
        name: 'Auditor',
        description: 'Read-only across financial records and audit logs.',
        scope: 'tenant',
        require2fa: true,
        permissions: [
            'audit.read', 'patient.read', 'encounter.read', 'rx.read', 'order.read',
            'admission.read', 'bed.read', 'charge.read', 'invoice.read',
            'claim.read', 'gl.read', 'stock.read', 'pharmacy.read', 'dues.read', 'nphies.messages.read',
            'authority.read', 'doctor_share.read',
            'lab.order.read', 'radiology.worklist', 'hr.employee.read',
            'report.read.all', 'report.financial', 'dashboard.read',
        ],
    },
    {
        code: 'mrd_officer',
        modules: ['mrd'],
        name: 'Medical Records Officer',
        description: 'Record completion tracking, ICD coding and consented record release.',
        scope: 'tenant',
        require2fa: false,
        permissions: [
            'patient.read', 'patient.merge', 'encounter.read', 'admission.read',
            'mrd.read', 'mrd.code', 'mrd.release', 'mortality.read',
            'report.read', 'dashboard.read', 'hr.self.read',
        ],
    },
    {
        code: 'support_staff',
        name: 'Support Staff',
        description: 'Ward boy, driver or housekeeping. Assigned task list only.',
        scope: 'department',
        require2fa: false,
        permissions: ['task.read', 'task.manage', 'housekeeping.manage', 'hr.self.read'],
    },
];
const NURSE_PERMISSIONS = [
    ...CLINICAL_READ, 'appointment.read', 'vitals.write', 'nursing.note.write',
    'admission.read', 'bed.read', 'emar.read', 'emar.administer',
    'lab.order.read', 'lab.collect', 'indent.request',
    'task.read', 'task.manage', 'report.read', 'dashboard.read', 'hr.self.read',
];
ROLE_TEMPLATES.push({
    code: 'phlebotomist',
    modules: ['lab'],
    name: 'Phlebotomist',
    description: 'Collects samples and records collection. No result entry.',
    scope: 'department',
    require2fa: false,
    permissions: ['patient.read', 'lab.order.read', 'lab.collect', 'dashboard.read', 'hr.self.read'],
}, {
    code: 'blood_bank_officer',
    modules: ['blood_bank'],
    name: 'Blood Bank Officer',
    description: 'Donors, blood stock, cross-match and issue.',
    scope: 'department',
    require2fa: false,
    permissions: ['patient.read', 'blood_bank.manage', 'lab.order.read', 'dashboard.read', 'hr.self.read'],
}, {
    code: 'midwife',
    modules: ['maternity'],
    name: 'Midwife',
    description: 'Labour ward care: partograph, delivery and newborn records, with nursing duties.',
    scope: 'department',
    require2fa: false,
    permissions: [...NURSE_PERMISSIONS, 'maternity.manage'],
}, {
    code: 'dietitian',
    modules: ['dietary'],
    name: 'Dietitian',
    description: 'Diet orders and meal planning for admitted patients.',
    scope: 'department',
    require2fa: false,
    permissions: ['patient.read', 'admission.read', 'dietary.manage', 'task.read', 'hr.self.read'],
}, {
    code: 'ambulance_coordinator',
    modules: ['ambulance'],
    name: 'Ambulance Coordinator',
    description: 'Fleet, dispatch and trip logs.',
    scope: 'branch',
    require2fa: false,
    permissions: ['ambulance.manage', 'task.read', 'hr.self.read'],
}, {
    code: 'patient_relations',
    modules: ['feedback'],
    name: 'Patient Relations',
    description: 'Feedback and complaints: route, answer and close them.',
    scope: 'branch',
    require2fa: false,
    permissions: ['feedback.read', 'feedback.manage', 'notification.send', 'hr.self.read'],
}, {
    code: 'housekeeping_supervisor',
    modules: ['housekeeping'],
    name: 'Housekeeping Supervisor',
    description: 'Linen, cleaning and bed turnaround tasks.',
    scope: 'branch',
    require2fa: false,
    permissions: ['housekeeping.manage', 'bed.read', 'task.read', 'task.manage', 'hr.self.read'],
});
const TEMPLATE_META = {
    super_admin: {
        groupModule: 'core',
        seed: 'always',
        allLive: true,
        description: 'Top administrator of the facility. Always has everything your plan includes.',
    },
    branch_admin: {
        groupModule: 'core',
        seed: 'multi_branch',
        addPermissions: ['notification.template.manage'],
        description: 'Administers one branch. Cannot change facility-wide settings or create higher administrators.',
    },
    receptionist: {
        groupModule: 'core',
        seed: 'always',
        modules: ['opd', 'ipd', 'er', 'lab', 'radiology', 'dental'],
        name: 'Front desk',
        description: 'Registration, bookings, token issue and the counter. No clinical data.',
    },
    cashier: { groupModule: 'billing', seed: 'always' },
    doctor: { groupModule: 'opd', seed: 'always', licensed: true },
    medical_officer: { groupModule: 'opd', seed: 'always', licensed: true },
    nurse: { groupModule: 'opd', seed: 'always', licensed: true },
    head_nurse: { groupModule: 'ipd', seed: 'always', licensed: true },
    ot_nurse: { groupModule: 'ot', seed: 'always', licensed: true },
    pharmacist: { groupModule: 'pharmacy', seed: 'always', licensed: true },
    store_keeper: { groupModule: 'inventory', seed: 'always' },
    lab_technician: { groupModule: 'lab', seed: 'always', licensed: true },
    pathologist: { groupModule: 'lab', seed: 'always', licensed: true },
    radiology_technician: { groupModule: 'radiology', seed: 'always', licensed: true },
    radiologist: { groupModule: 'radiology', seed: 'always', licensed: true },
    insurance_officer: { groupModule: 'insurance', seed: 'optional' },
    mrd_officer: { groupModule: 'mrd', seed: 'optional' },
    hr_officer: { groupModule: 'hr', seed: 'optional' },
    accountant: { groupModule: 'accounting', seed: 'optional' },
    auditor: { groupModule: 'core', seed: 'optional' },
    support_staff: {
        groupModule: 'core',
        seed: 'optional',
        modules: ['ipd', 'er', 'ot', 'housekeeping'],
        name: 'Porter / support staff',
        description: 'Porter, driver or housekeeping. Assigned task list only.',
    },
    phlebotomist: { groupModule: 'lab', seed: 'optional', licensed: true },
    blood_bank_officer: { groupModule: 'blood_bank', seed: 'always', licensed: true },
    midwife: { groupModule: 'maternity', seed: 'optional', licensed: true },
    dietitian: { groupModule: 'dietary', seed: 'optional' },
    ambulance_coordinator: { groupModule: 'ambulance', seed: 'optional' },
    patient_relations: { groupModule: 'feedback', seed: 'optional' },
    housekeeping_supervisor: { groupModule: 'housekeeping', seed: 'optional' },
};
for (const template of ROLE_TEMPLATES) {
    const meta = TEMPLATE_META[template.code];
    if (!meta) {
        template.groupModule = template.groupModule ?? template.modules?.[0] ?? 'core';
        template.seed = template.seed ?? 'optional';
        continue;
    }
    template.groupModule = meta.groupModule;
    template.seed = meta.seed;
    if (meta.licensed)
        template.licensed = true;
    if (meta.allLive)
        template.allLive = true;
    if (meta.modules)
        template.modules = meta.modules;
    if (meta.name)
        template.name = meta.name;
    if (meta.description)
        template.description = meta.description;
    if (meta.addPermissions) {
        template.permissions = [...new Set([...template.permissions, ...meta.addPermissions])];
    }
}
/** A role holding `*` bypasses individual permission checks inside its tenant. */
export const WILDCARD_PERMISSION = '*';
export const ROLE_TEMPLATE_INDEX = new Map(ROLE_TEMPLATES.map((r) => [r.code, r]));
/**
 * The templates a facility with these modules should have. Core roles always;
 * a module's roles only once that module is licensed — and when a module is
 * added later, calling this again says which roles are now missing.
 */
export function roleTemplatesForModules(modules) {
    const enabled = new Set(modules);
    return ROLE_TEMPLATES.filter((role) => !role.modules || role.modules.some((m) => enabled.has(m)));
}
export function seedsByDefault(template, modules, context) {
    if (template.code === 'insurance_officer' && modules.has('nphies'))
        return true;
    if (template.code === 'support_staff' && (modules.has('ipd') || modules.has('housekeeping')))
        return true;
    if (template.seed === 'multi_branch')
        return context.branchCount > 1;
    return template.seed !== 'optional';
}
export function seedTemplatesForModules(modules, context) {
    const enabled = new Set(modules);
    return roleTemplatesForModules(enabled).filter((template) => seedsByDefault(template, enabled, context));
}
export function roleGroupModule(template, enabled) {
    const own = template.groupModule ?? 'core';
    if (own === 'core' || enabled.has(own))
        return own;
    return template.modules?.find((module) => enabled.has(module)) ?? own;
}
//# sourceMappingURL=roles.js.map